import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import properties from "../../../../apiConfigs/properties";
import Loading from "../../../../designComponents/Loading";
import requestAPI from "../../../../utils/requestAPI";
import { LabelText, SectionTitle } from "../styles";
import { ChangeLogSection } from "./ChangeLogSection";
import { IPropertyChangeLog, PropertyChangeLogPagination } from "./types";
import Modal from "../../../../designComponents/Modal";
import Button from "../../../../designComponents/Button";
import Typography from "../../../../designComponents/Typography";

const initialPagination: PropertyChangeLogPagination = {
  currentPage: 1,
  limit: 10,
  nextPage: 2,
  prevPage: null,
  results: 30,
  total: 30,
  totalPages: 1,
};

function History() {
  const paginationTriggerRef = useRef<HTMLDivElement>(null);
  const { propertyId } = useParams();
  const [changeLogs, setChangeLogs] = useState<IPropertyChangeLog[]>([]);
  const [pagination, setPagination] =
    useState<PropertyChangeLogPagination>(initialPagination);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false); // for next page loading
  const [historyModal, setHistoryModal] = useState(false);

  const getPropertyHistoryDetails = async (id: string, page = 1) => {
    if (page === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const response = await requestAPI(
        properties.changeHistory(propertyId as string, {
          page,
          limit: pagination.limit,
        })
      );
      if (response) {
        if (page === 1) {
          setChangeLogs(response.logs);
        } else {
          setChangeLogs((prev) => {
            const combined = [...prev, ...response.logs];
            const uniqueLogs = Array.from(
              new Map(combined.map((log) => [log._id, log])).values()
            );
            return uniqueLogs;
          });
        }
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error("Failed to load property change logs", error);
    } finally {
      if (page === 1) setLoading(false);
      else setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (propertyId) {
      getPropertyHistoryDetails(propertyId);
    }
  }, [propertyId]);

  // Intersection Observer to detect bottom of scroll
  useEffect(() => {
    const scrollContainer = document.querySelector(".history-scroll-container");

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        console.log({
          isintersecting: target.isIntersecting,
          loadingMore,
          next: pagination.nextPage,
          curr: pagination.currentPage,
          tt: pagination.totalPages,
        });

        if (
          target.isIntersecting &&
          !loadingMore &&
          pagination.nextPage &&
          pagination.currentPage < pagination.totalPages
        ) {
          getPropertyHistoryDetails(propertyId as string, pagination.nextPage);
        }
      },
      {
        root: scrollContainer,
        rootMargin: "10px",
        threshold: 1.0,
      }
    );

    if (paginationTriggerRef.current) {
      observer.observe(paginationTriggerRef.current);
    }

    return () => {
      if (paginationTriggerRef.current) {
        observer.unobserve(paginationTriggerRef.current);
      }
    };
  }, [
    paginationTriggerRef,
    pagination,
    loadingMore,
    propertyId,
    loading,
    historyModal,
  ]);

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Button
        variant="gray"
        radius="normal"
        onClick={() => setHistoryModal(true)}
      >
        View Timeline
      </Button>
      <Modal
        isOpen={historyModal}
        onClose={() => {
          setHistoryModal(false);
        }}
      >
        <div
          className="history-scroll-container"
          style={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
            overflowY: "scroll",
          }}
        >
          <SectionTitle style={{ marginBottom: "30px" }}>
            Changes TimeLine
          </SectionTitle>

          {changeLogs?.length === 0 ? (
            <div>
              <LabelText>Nothing to display</LabelText>
            </div>
          ) : (
            changeLogs.map((clog, index) => (
              <ChangeLogSection
                key={index}
                date={clog.createdAt}
                title={clog.changes?.title}
                changeLog={clog}
              />
            ))
          )}

          {loadingMore && <LabelText>Loading more...</LabelText>}

          <div ref={paginationTriggerRef} style={{ marginTop: "20px" }}>
            <Typography fontWeight="bold" size={"title"}>
              Start
            </Typography>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default History;
